import React, { useState } from "react";
import {
  Input,
  Button,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import { useFormHandlers } from "@swordsweeper/react-forms";
import * as AuthApi from "@src/api/AuthApi";
import SpinningLoader from "@src/components/common/SpinningLoader";
import get from "lodash/get";

interface PasswordFormData {
  currentPassword?: string;
  newPassword?: string;
  newPassword2?: string;
}

interface PasswordFormErrors {
  currentPassword?: string[];
  newPassword?: string[];
  newPassword2?: string[];
}

export default function AccountSecuritySettings(): React.JSX.Element {
  const { showToast } = useToast();
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [formErrors, setFormErrors] = useState<PasswordFormErrors>({});
  const { formData, handleUpdate, setFormData, isValid } =
    useFormHandlers<PasswordFormData>({});

  const handleSaveChanges = async () => {
    setIsSavingPassword(true);
    setFormErrors({});
    try {
      const response = await AuthApi.changeCurrentUserPassword(formData);
      if (response.status === 200) {
        setFormData({
          currentPassword: "",
          newPassword: "",
          newPassword2: "",
        });
        showToast({
          message: "Password updated",
          variant: "success",
        });
      }
    } catch (e) {
      console.error(e);
      const responseBody = (e as any)?.response?.body;
      if (responseBody && typeof responseBody === "object") {
        setFormErrors(responseBody);
      } else {
        showToast({
          message: "Error changing password",
          variant: "error",
        });
      }
    } finally {
      setIsSavingPassword(false);
    }
  };

  const currentPasswordErrors = get(formErrors, "currentPassword", []);
  const hasCurrentPasswordError = currentPasswordErrors.length > 0;

  const newPasswordErrors = get(formErrors, "newPassword", []);
  const hasNewPasswordError = newPasswordErrors.length > 0;

  const newPassword2Errors = get(formErrors, "newPassword2", []);
  const hasNewPassword2Error = newPassword2Errors.length > 0;

  return (
    <div>
      {isSavingPassword && <SpinningLoader />}

      <div className="text-lg font-weight-bold padding-y-lg">
        Change Your Password
      </div>
      <div className="flex-column spaced-content-v-xl">
        <Input
          defaultValue=""
          onChange={(value) => handleUpdate({ name: "currentPassword", value })}
          value={formData.currentPassword}
          width={340}
          label="Current Password"
          type="password"
          error={hasCurrentPasswordError}
          data-testid="account-security-settings-current-password-input"
        />
        {hasCurrentPasswordError && (
          <div className="text-xs text-error font-weight-bold">
            {currentPasswordErrors.map((e, i) => (
              <div
                key={`account-security-settings-current-password-error-${i}`}
                data-testid={`account-security-settings-current-password-error-${i}`}
              >
                {e}
              </div>
            ))}
          </div>
        )}
        <Input
          defaultValue=""
          onChange={(value) => handleUpdate({ name: "newPassword", value })}
          value={formData.newPassword}
          width={340}
          label="Enter New Password"
          type="password"
          error={hasNewPasswordError}
          data-testid="account-security-settings-new-password-input"
        />
        {hasNewPasswordError && (
          <div className="text-xs text-error font-weight-bold">
            {newPasswordErrors.map((e, i) => (
              <div
                key={`account-security-settings-new-password-error-${i}`}
                data-testid={`account-security-settings-new-password-error-${i}`}
              >
                {e}
              </div>
            ))}
          </div>
        )}
        <Input
          defaultValue=""
          onChange={(value) => handleUpdate({ name: "newPassword2", value })}
          value={formData.newPassword2}
          width={340}
          label="Confirm New Password"
          type="password"
          error={hasNewPassword2Error}
        />
        {hasNewPassword2Error && (
          <div className="text-xs text-error font-weight-bold">
            {newPassword2Errors.map((e, i) => (
              <div
                key={`account-security-settings-new-password2-error-${i}`}
                data-testid={`account-security-settings-new-password2-error-${i}`}
              >
                {e}
              </div>
            ))}
          </div>
        )}
        <div>
          <Button
            className="btn btn-primary btn-md"
            label="Save Changes"
            onClick={handleSaveChanges}
            disabled={!isValid}
            data-testid="account-security-settings-save-button"
          />
        </div>
      </div>
    </div>
  );
}
